/**
 * A refused write as the panel shows it: the server's sentence, and the datasets it names when the
 * dataset guard refused, so each name can lead to its dataset.
 */

import { StreamFailure } from '@/lib/sse';
import { failureMessage } from '../format';

interface DatasetInUse {
	datasetId: string;
	name: string;
}

export interface Refusal {
	message: string;
	datasets: DatasetInUse[];
}

const isDatasetInUse = (entry: unknown): entry is DatasetInUse =>
	typeof entry === 'object' &&
	entry !== null &&
	typeof (entry as DatasetInUse).datasetId === 'string' &&
	typeof (entry as DatasetInUse).name === 'string';

/** The ApiError's `errors`, from a stream or from a REST response. */
const detailOf = (err: unknown): unknown => {
	if (err instanceof StreamFailure) return err.errors;
	if (err && typeof err === 'object' && 'response' in err) {
		return (err as { response?: { data?: { errors?: unknown } } }).response?.data?.errors;
	}
	return undefined;
};

export const refusalOf = (err: unknown, fallback: string): Refusal => {
	const detail = detailOf(err);
	return {
		message: failureMessage(err, fallback),
		datasets: Array.isArray(detail) ? detail.filter(isDatasetInUse) : []
	};
};

type RefusalPart = { text: string } | { dataset: DatasetInUse };

/** Where the sentence names a dataset, quoted or bare, in the order given. */
const placeOf = (text: string, name: string): { at: number; skip: number } | null => {
	const quoted = text.indexOf(`"${name}"`);
	if (quoted >= 0) return { at: quoted + 1, skip: name.length };
	const bare = text.indexOf(name);
	return bare >= 0 ? { at: bare, skip: name.length } : null;
};

/** The sentence cut around each dataset it names; one it does not name follows it. */
export const refusalParts = ({ message, datasets }: Refusal): RefusalPart[] => {
	const parts: RefusalPart[] = [];
	const unnamed: DatasetInUse[] = [];
	let rest = message;
	for (const dataset of datasets) {
		const place = dataset.name ? placeOf(rest, dataset.name) : null;
		if (!place) {
			unnamed.push(dataset);
			continue;
		}
		if (place.at > 0) parts.push({ text: rest.slice(0, place.at) });
		parts.push({ dataset });
		rest = rest.slice(place.at + place.skip);
	}
	if (rest) parts.push({ text: rest });
	unnamed.forEach((dataset, index) => parts.push({ text: index === 0 ? ' ' : ', ' }, { dataset }));
	return parts;
};
